import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';

// helpers
import styled from 'styled-components';
import useTranslation from 'hooks/useTranslation';
import { useDispatch } from 'react-redux';
import { customHistory } from 'constants/customHistory';
import { useFormikContext } from 'formik';
import { changeView as changeViewRedux } from 'redux/actions/view';
import {
  CHANGE_VIEW_TYPES,
  removeChangeViewHandler,
  setChangeViewHandler,
} from 'redux/actions/view';

// components
import { Modal } from 'antd';
import { Button, Col, Row } from '@ui';

interface IChangeView {
  changeView?: () => void;
  type?: CHANGE_VIEW_TYPES;
}

// Show confirmation modal if form is dirty and user leaves without saving
const ConfirmationExitWithoutSaving = () => {
  const { t } = useTranslation(['form', 'common', 'onboarding']);
  const form = useFormikContext();
  const dispatch = useDispatch();

  const { dirty, isValid, submitCount, resetForm, isSubmitting } = form;

  const [changeView, setChangeView] = useState<IChangeView | undefined>();
  const [lastSubmitCount, setLastSubmitCount] = useState(submitCount);

  useEffect(() => {
    if (dirty || !isValid) {
      dispatch(setChangeViewHandler(onOpenModal));
    } else {
      dispatch(removeChangeViewHandler());
    }
  }, [dirty, isValid]);

  useEffect(() => {
    if (lastSubmitCount !== submitCount) {
      setLastSubmitCount(submitCount);
      resetForm(form);
    }
  }, [submitCount]);

  useEffect(() => {
    let unblock: (() => void) | undefined;

    if (dirty || !isValid) {
      unblock = customHistory.block((tx) => {
        dispatch(
          changeViewRedux(() => {
            unblock?.();
            tx.retry();
          }, CHANGE_VIEW_TYPES.ROUTE),
        );
      });
    }

    return () => {
      unblock?.();

      dispatch(removeChangeViewHandler());
    };
  }, [dirty, isValid]);

  const onOpenModal = (changeView?: () => void, type?: CHANGE_VIEW_TYPES) => {
    setChangeView({ changeView, type });
  };

  const onCancel = useCallback(() => {
    setChangeView(undefined);
  }, [changeView]);

  const onNo = useCallback(() => {
    setChangeView(undefined);
    resetForm(form);
    changeView?.changeView && changeView.changeView();
  }, [changeView]);

  const modalWordings = useMemo<{
    title?: string;
    content: React.ReactNode;
    cancelBtn: string;
    noBtn: string;
  }>(() => {
    switch (changeView?.type) {
      case CHANGE_VIEW_TYPES.MODAL:
        return {
          title: undefined,
          content: t('confirm_exit_without_save.title'),
          cancelBtn: t('stay_on_page', { ns: 'common' }),
          noBtn: t('discard_changes_and_leave', { ns: 'common' }),
        };

      case CHANGE_VIEW_TYPES.ONBOARDING_VERIFICATION_PAGE:
        return {
          title: t('confirmation_modal.title', { ns: 'onboarding' }),
          content: t('confirmation_modal.description', { ns: 'onboarding' }),
          cancelBtn: t('stay_on_page', { ns: 'common' }),
          noBtn: t('discard_changes_and_leave', { ns: 'common' }),
        };

      default:
        return {
          title: undefined,
          content: t('confirm_exit_without_save.title'),
          cancelBtn: t('stay_on_page', { ns: 'common' }),
          noBtn: t('discard_changes_and_leave', { ns: 'common' }),
        };
    }
  }, [changeView?.type]);

  return (
    <StyledModal
      zIndex={9999}
      title={modalWordings.title}
      closable={false}
      open={!!changeView}
      onCancel={onCancel}
      footer={
        <Row justify="end" gutter={[16, 0]}>
          <Col>
            <Button onClick={onCancel} type="bordered" disabled={isSubmitting}>
              {modalWordings.cancelBtn}
            </Button>
          </Col>
          <Col>
            <Button onClick={onNo} danger disabled={isSubmitting}>
              {modalWordings.noBtn}
            </Button>
          </Col>
        </Row>
      }
    >
      <StyledModalContent>{modalWordings.content}</StyledModalContent>
    </StyledModal>
  );
};

const StyledModalContent = styled.div`
  font-size: ${({ theme }) => theme.fontSizeSm};
`;

const StyledModal = styled(Modal)`
  .ant-modal-body {
    padding-top: 0;
  }

  .ant-modal-footer {
    padding-top: 0;
  }
`;

export default memo(ConfirmationExitWithoutSaving);
